import React, { memo, useCallback, useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import BackendInfiniteScroll from "../../../../components/BackendInfiniteScroll/BackendInfiniteScroll";
import { Button } from "../../../../components/Button";
import Dropdown from "../../../../components/Dropdowns/Dropdown";
import { Input } from "../../../../components/Input";
import Loading from "../../../../components/Loading/Loading";
import Produto from "../../../../components/Produto";
import { Select } from "../../../../components/Select";
import SkeletonProdutos from "../../../../components/Skeletons/SkeletonProdutos/SkeletonProdutos";
import Slider from "../../../../components/Slider/Slider";
import { CatalogoContext } from "../../../../contexts/Catalogo/CatalogoContext";
import { ProdutosContext } from "../../../../contexts/Produtos/ProdutosContext";
import { Produto as ProdutoType } from "../../../../types/Produto";
import { produtosApi, GetProdutosPaginadoParams } from "../../../../hooks/produtosApi";
import "./styles";
import ProdutosListagem from "./styles";
interface ProdutosProps {
  produtosCatalogo: number[];
  onAdicionarProdutos: (ids_produtos: number[]) => void;
  onRemoverProdutos: (ids_produtos: number[]) => void;
}

const Produtos: React.FC<ProdutosProps> = ({
  produtosCatalogo,
  onAdicionarProdutos,
  onRemoverProdutos,
}) => {
  const {
    loading,
    setLoading,
    produtosFiltrados,
    filtros,
    setFiltros,
    filtroPesquisaAux,
    setFiltroPesquisaAux,
    optionsVisualizacao,
    optionsEstoque,
    optionsOrdenacao,
    produtos,
    categorias,
    init,
    hasMore,
    fetchMore,
    loadingMore,
  } = useContext(ProdutosContext);

  const context = useContext(CatalogoContext);
  const catalogo = context ? context.catalogo : null;
  const [maiorValor, setMaiorValor] = React.useState(100);
  const [maiorEstoque, setMaiorEstoque] = React.useState(100);
  const listasDePreco = context ? context.listasDePrecos : null;

  let moeda = "R$";
  if (context) {
    let listaCatalogo = listasDePreco?.filter(
      (lista) => lista.id === catalogo?.id_tabela
    );
    if (listaCatalogo?.length) moeda = listaCatalogo[0].moeda;
  }

  const handleAdicionarProdutos = useCallback(
    (ids_produtos: number[]) => {
      onAdicionarProdutos(ids_produtos);
    },
    [onAdicionarProdutos]
  );

  const handleRemoverProdutos = useCallback(
    (ids_produtos: number[]) => {
      onRemoverProdutos(ids_produtos);
    },
    [onRemoverProdutos]
  );

  const [loadingTodos, setLoadingTodos] = useState(false);

  const adicionarRemoverTodos = useCallback(
    async (adicionar: boolean) => {
      setLoadingTodos(true);
      try {
        const params: GetProdutosPaginadoParams = {
          search: filtros.filtroPesquisa,
          categoria: filtros.filtroCategoria,
          ativos: "1",
          estoque: filtros.filtroEstoque,
          estoque_min: filtros.filtroEstoqueMin,
          estoque_max: filtros.filtroEstoqueMax,
          tag: filtros.filtroTag,
          order: filtros.filtroOrdenacao,
        };

        if (catalogo) {
          params.id_tabela = catalogo.id_tabela;
          params.visualizacao = filtros.filtroVisualizacao;
          params.preco_min = filtros.filtroPrecoMin;
          params.preco_max = filtros.filtroPrecoMax;
          params.id_catalogo = catalogo.id;
          params.promocao = filtros.filtroPromocao;
        }

        const ids = await produtosApi().getIdsFiltrados(params);

        if (adicionar) {
          handleAdicionarProdutos(ids);
        } else {
          handleRemoverProdutos(ids);
        }
      } catch (error) {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar todos os produtos. Tente novamente.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoadingTodos(false);
      }
    },
    [filtros, catalogo, handleAdicionarProdutos, handleRemoverProdutos]
  );

  const duration = 250;
  const handleAplicarFiltro = () => {
    if (!catalogo?.id_tabela) {
      Swal.fire({
        title: "Atenção!",
        text: "Você precisa selecionar uma tabela de preços antes de aplicar os filtros.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }
    init();
    setFiltroAplicado(true);
    setTimeout(() => {
      window.scrollTo({
        top:
          window.scrollY +
          produtosScroll.current!.getBoundingClientRect().top -
          100,
        behavior: "smooth",
      });
    }, duration);
  };

  const [filtroAplicado, setFiltroAplicado] = React.useState(false);
  const produtosScroll = React.useRef<HTMLDivElement>(null);

  // Buscar ranges de preço e estoque da API
  // Os filtros enviados aqui precisam ser os mesmos da listagem, senão o slider
  // abre em um valor que não corresponde ao produto mais caro exibido e acaba
  // escondendo produtos que deveriam aparecer.
  useEffect(() => {
    const fetchRanges = async () => {
      if (!catalogo?.id_tabela) return;

      try {
        const ranges = await produtosApi().getFiltrosRange({
          id_tabela: catalogo.id_tabela,
          id_catalogo: catalogo?.id,
          search: filtros.filtroPesquisa,
          categoria: filtros.filtroCategoria,
          ativos: filtros.filtroAtivos,
          tag: filtros.filtroTag,
          visualizacao: filtros.filtroVisualizacao,
          promocao: filtros.filtroPromocao,
          estoque: filtros.filtroEstoque,
          estoque_min: filtros.filtroEstoqueMin,
          estoque_max: filtros.filtroEstoqueMax,
        });

        const novoMaiorValor = ranges.preco_max > 0 ? ranges.preco_max : 0;
        setMaiorValor(novoMaiorValor);

        // Sempre ancorar o filtro de preço em 0 → maior valor do recorte atual
        // (não dispara re-render quando já está nesse intervalo)
        setFiltros((prev) =>
          prev.filtroPrecoMin === 0 && prev.filtroPrecoMax === novoMaiorValor
            ? prev
            : { ...prev, filtroPrecoMin: 0, filtroPrecoMax: novoMaiorValor }
        );

        if (ranges.estoque_max > 0) {
          setMaiorEstoque(ranges.estoque_max);
        }
      } catch (error) {
        console.error("Erro ao buscar ranges de filtros:", error);
      }
    };

    fetchRanges();
  }, [
    catalogo?.id_tabela,
    catalogo?.id,
    filtros.filtroPesquisa,
    filtros.filtroCategoria,
    filtros.filtroAtivos,
    filtros.filtroTag,
    filtros.filtroVisualizacao,
    filtros.filtroPromocao,
    filtros.filtroEstoque,
    filtros.filtroEstoqueMin,
    filtros.filtroEstoqueMax,
  ]);

  const replicado =
    catalogo &&
    catalogo.id_catalogo_original &&
    catalogo.id_catalogo_original !== "0"
      ? true
      : false;

  return (
    <>
      <ProdutosListagem>
        <div className="grid-header">
          <div className="input">
            <Input
              label="Pesquisar"
              type={"text"}
              icone="icons/search.svg"
              placeholder={"Por SKU ou descrição"}
              value={filtroPesquisaAux}
              onChange={(newValue) => {
                newValue === filtros.filtroPesquisa
                  ? setLoading(false)
                  : setLoading(true);
                setFiltroPesquisaAux(newValue);
              }}
              onDebounce={(newValue) =>
                setFiltros({ ...filtros, filtroPesquisa: newValue })
              }
              debounceTime={500}
            />
          </div>
          <div className="select">
            <Select
              sizeLabel="medium"
              label="Filtrar por categoria"
              options={categorias}
              value={filtros.filtroCategoria}
              onChange={(newValue) => {
                setFiltros({ ...filtros, filtroCategoria: newValue });
              }}
            />
          </div>
        </div>
        <div>
          {!filtroAplicado && (
            <Button
              title="Aplicar filtros"
              className="button-filtrar fonteTexto"
              width="100%"
              onClick={() => handleAplicarFiltro()}
            ></Button>
          )}
        </div>
        <Dropdown active={filtroAplicado} animated={true} duration={duration}>
          <div className={`produtos-wrapper`}>
            <div className="informacoes">
              <div className="flex-container-produtos fonteRotulos">
                <span>{produtosFiltrados.length}{hasMore ? '+' : ''} produtos encontrados</span>
                <div>{produtosCatalogo.length} adicionados ao seu catálogo</div>
              </div>
              <div className="flex-container-produtos fonteTexto">
                <Slider
                  min={0}
                  max={maiorValor}
                  step={0.01}
                  value={[
                    filtros.filtroPrecoMin ?? 0,
                    filtros.filtroPrecoMax ?? maiorValor
                  ]}
                  onDebounceStart={(value) => {
                    setLoading(true);
                  }}
                  onDebounce={(value) => {
                    setFiltros({
                      ...filtros,
                      filtroPrecoMin: value[0],
                      filtroPrecoMax: value[1],
                    });
                  }}
                />
              </div>
              <div className="flex-container-produtos">
                <div className="container-half">
                  <Select
                    options={optionsVisualizacao}
                    label="Visualizar"
                    value={filtros.filtroVisualizacao}
                    onChange={(newValue) => {
                      setFiltros({ ...filtros, filtroVisualizacao: newValue });
                    }}
                  />
                </div>
                <div className="container-half">
                  <Select
                    options={optionsOrdenacao}
                    label="Ordenar"
                    value={filtros.filtroOrdenacao}
                    onChange={(newValue) => {
                      setFiltros({ ...filtros, filtroOrdenacao: newValue });
                    }}
                  />
                </div>
                <div className="container-half">
                  <Select
                    options={optionsEstoque}
                    label="Estoque"
                    value={filtros.filtroEstoque}
                    onChange={(newValue) => {
                      setFiltros({ ...filtros, filtroEstoque: newValue });
                    }}
                  />
                </div>
              </div>
              {filtros.filtroEstoque === "personalizado" && (
                <div className="flex-container-produtos fonteTexto">
                  <Slider
                    min={0}
                    max={maiorEstoque}
                    step={1}
                    formatType="integer"
                    value={[
                      filtros.filtroEstoqueMin ?? 0,
                      filtros.filtroEstoqueMax ?? maiorEstoque
                    ]}
                    onDebounceStart={() => setLoading(true)}
                    onDebounce={(value) => {
                      setFiltros({
                        ...filtros,
                        filtroEstoqueMin: value[0],
                        filtroEstoqueMax: value[1],
                      });
                    }}
                  />
                </div>
              )}
              <div className="flex-container-produtos btn-adicionar-remover">
                <div className="container-half">
                  <Button
                    width="100%"
                    disabled={replicado || loadingTodos}
                    loading={loadingTodos}
                    className="botao fonteTexto"
                    title="Adicionar Todos"
                    onClick={() => adicionarRemoverTodos(true)}
                  ></Button>
                </div>
                <div className="container-half">
                  <Button
                    width="100%"
                    disabled={replicado || loadingTodos}
                    loading={loadingTodos}
                    className="botao cinza fonteTexto"
                    title="Remover Todos"
                    onClick={() => adicionarRemoverTodos(false)}
                  ></Button>
                </div>
              </div>
            </div>
            <div ref={produtosScroll} className="produtos scrollBonito">
              {loading ? (
                <SkeletonProdutos />
              ) : (
                <>
                  <BackendInfiniteScroll
                    next={fetchMore}
                    hasMore={hasMore}
                    loader={<Loading relative />}
                  >
                    <div className="produtos-wrapper">
                      {produtosFiltrados.map((produto: ProdutoType, index) => (
                        <div className="produto-container" key={produto.id}>
                          <Produto
                            produto={produto}
                            moeda={moeda}
                            openImage={catalogo ? true : false}
                          >
                            <div className="button-wrapper">
                              {produtosCatalogo.includes(produto.id) ? (
                                <Button
                                  className="botao-remover fonteTexto"
                                  disabled={replicado}
                                  onClick={() =>
                                    handleRemoverProdutos([produto.id])
                                  }
                                  title={""}
                                />
                              ) : (
                                <Button
                                  className="botao-adicionar fonteTexto"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAdicionarProdutos([produto.id]);
                                  }}
                                  title=""
                                />
                              )}
                            </div>
                          </Produto>
                        </div>
                      ))}
                    </div>
                  </BackendInfiniteScroll>
                </>
              )}
              {!loading && !loadingMore && produtosFiltrados.length === 0 && (
                <div className="mensagem">
                  <div className="texto-titulo fonteSubtitulo">
                    <div>Nenhum resultado encontrado para sua busca.</div>
                  </div>
                  <div className="texto-subtitulo fonteRotulos">
                    <div>Tente novamente com uma busca diferente!</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Dropdown>
      </ProdutosListagem>
    </>
  );
};

export default memo(Produtos);
